import type { Task } from "@app/core";
import { useEffect, useState } from "react";
import { useI18n } from "../../../lib/useI18n.js";
import { CalendarField } from "../CalendarField.js";
import { LabelField } from "../LabelField.js";
import { TimeZoneField } from "../TimeZoneField.js";

interface Props {
  editing: Task | null;
  isEvent: boolean;
  calendarId: string | null;
  onCalendar: (id: string) => void;
  labelIds: string[];
  onLabels: (ids: string[]) => void;
  tz: string | null;
  onTz: (tz: string | null) => void;
  error: string | null;
  submitting: boolean;
  onCancel: () => void;
  onSubmit: () => void;
  onDelete: () => void;
  onComplete: () => void;
}

/** Advanced disclosure (calendar · labels · time zone), error line and the
 *  action buttons at the card's foot. */
export function CardFooter({
  editing, isEvent, calendarId, onCalendar, labelIds, onLabels, tz, onTz,
  error, submitting, onCancel, onSubmit, onDelete, onComplete,
}: Props) {
  const { t } = useI18n();
  const [showAdvanced, setShowAdvanced] = useState(false);
  useEffect(() => { setShowAdvanced(false); }, [editing]);

  return (
    <>
      <div className="field">
        <button type="button" className="adv-toggle" onClick={() => setShowAdvanced((v) => !v)}>
          {showAdvanced ? "▾" : "▸"} {t("taskForm.advancedOptions")}
        </button>
        {showAdvanced && (
          <div className="adv-body">
            <CalendarField value={calendarId} onChange={onCalendar} />
            <LabelField value={labelIds} onChange={onLabels} />
            <TimeZoneField value={tz} onChange={onTz} />
          </div>
        )}
      </div>

      {error && <p className="form-error">{error}</p>}

      <div className="actions">
        <div className="actions-side">
          {editing && (
            <button type="button" className="btn danger" onClick={onDelete}>{t("common.delete")}</button>
          )}
          {editing && (
            <button type="button" className="btn" onClick={onComplete}>
              {editing.status === "done" ? t("taskForm.markUncomplete") : t("taskForm.markComplete")}
            </button>
          )}
        </div>
        <div className="actions-side">
          <button type="button" className="btn" onClick={onCancel}>{t("common.cancel")}</button>
          <button type="button" className="btn primary" onClick={onSubmit} disabled={submitting}>
            {submitting ? t("taskForm.saving") : editing ? t("common.save") : t(isEvent ? "taskForm.createEvent" : "taskForm.createTask")}
          </button>
        </div>
      </div>
    </>
  );
}
